/**
 * EventEditorWizard — the "Cadillac" event creation/edit flow.
 *
 * Renders a 7-step wizard:
 *   1. Template picker     — pre-fills form from EVENT_TEMPLATES catalog.
 *   2. Basics              — title, type, intention, cover, tags, description.
 *   3. When                — date/time + recurrence rule + live preview of the
 *                            next 5 occurrences via expandRecurrence().
 *   4. Where               — location/island; option to copy from listing.
 *   5. Tickets             — pricing model, capacity, doubloons.
 *   6. Linked redeemable   — optionally attach a redeemable to this event
 *                            (link is stored canonically on the redeemable
 *                            via the existing redeemableItems editor flow;
 *                            the wizard only previews any current link).
 *   7. Review              — final summary, "Save Draft" / "Publish".
 *
 * The Cloud Function does the actual publish/draft promotion: when all
 * required fields are present the server stores status='published';
 * otherwise it stores status='draft'. There is no admin approval gate.
 */

import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarPlus,
  faChevronLeft,
  faChevronRight,
  faSpinner,
  faCheck,
  faTimes,
  faInfoCircle,
  faImage,
  faMapMarkerAlt,
  faTicketAlt,
  faGift,
  faRepeat,
  faStar,
  faLocationCrosshairs,
  faStore,
  faCrown,
} from "@fortawesome/free-solid-svg-icons";

import EventLocationPickerModal from "./EventLocationPickerModal";
import PromotionStackPanel from "./PromotionStackPanel";

import {
  EVENT_TYPES,
  EVENT_TYPE_KEYS,
  ISLANDS,
  RECURRENCE_FREQ,
  RECURRENCE_FREQ_OPTIONS,
  DAYS_OF_WEEK,
  EVENT_INTENTIONS,
} from "../../constants/events";
import {
  EVENT_TEMPLATES,
  TEMPLATE_CATEGORIES,
  getEventTemplate,
} from "../../constants/eventTemplates";
import { expandRecurrence, describeRecurrence } from "../../utils/recurrence";
import { GATING_TRAVELER_TIERS } from "../../constants/plans";
import { createEvent, updateEvent } from "../../utils/events";
import { formatListingAddress, safeText } from "../../utils/formatAddress";
import { useToast } from "../ui/Toast";

import "../../styles/EventEditorWizard.css";

const STEPS = [
  { key: "template", label: "Template" },
  { key: "basics", label: "Basics" },
  { key: "when", label: "When" },
  { key: "where", label: "Where" },
  { key: "tickets", label: "Tickets" },
  { key: "redeemable", label: "Linked Offer" },
  { key: "review", label: "Review" },
];

function toLocalInputValue(value) {
  if (!value) return "";
  let date;
  if (typeof value === "number") date = new Date(value);
  else if (value.toDate) date = value.toDate();
  else if (value._seconds) date = new Date(value._seconds * 1000);
  else date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromLocalInputValue(value) {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Apply a template's defaults to the in-progress form. Preserves anything
 * the user has already typed unless `force` is true.
 */
function applyTemplate(form, template, listing) {
  if (!template) return form;
  const d = template.defaults || {};
  // Compose startAt from today + timeWindow.start (if provided) so the
  // wizard has a sensible default that's editable.
  let startAt = form.startAt;
  let endAt = form.endAt;
  if (!startAt && d.timeWindow?.start) {
    const today = new Date();
    today.setDate(today.getDate() + 1); // tomorrow
    const [h, m] = d.timeWindow.start.split(":").map(Number);
    today.setHours(h || 17, m || 0, 0, 0);
    startAt = toLocalInputValue(today);
    if (d.timeWindow.end) {
      const end = new Date(today);
      const [eh, em] = d.timeWindow.end.split(":").map(Number);
      end.setHours(eh || 19, em || 0, 0, 0);
      if (end < today) end.setDate(end.getDate() + 1);
      endAt = toLocalInputValue(end);
    } else if (d.durationMinutes) {
      endAt = toLocalInputValue(new Date(today.getTime() + d.durationMinutes * 60000));
    }
  }
  return {
    ...form,
    templateKey: template.key,
    title: form.title || d.title || "",
    description: form.description || d.description || "",
    type: d.type || form.type || EVENT_TYPE_KEYS.PUBLIC,
    intention: form.intention || d.intention || "",
    tags: form.tags && form.tags.length > 0 ? form.tags : (d.tags || []),
    recurrence: form.recurrence || d.recurrence || null,
    durationMinutes: form.durationMinutes || d.durationMinutes || null,
    capacity: form.capacity || d.capacity || "",
    doubloonReward: form.doubloonReward || d.doubloonReward || 0,
    ticketPriceCents: form.ticketPriceCents || (d.ticketPriceCents ? d.ticketPriceCents / 100 : 0),
    graceWindowMinutes: d.graceWindowMinutes || form.graceWindowMinutes,
    suggestedRedeemable: d.suggestedRedeemable || null,
    startAt,
    endAt,
    location: form.location || listing?.location || formatListingAddress(listing?.address) || "",
    island: form.island || (listing?.island || ""),
  };
}

const LOCATION_MODES = {
  BUSINESS: "business",
  ADDRESS: "address",
  COORDINATES: "coordinates",
};

const EMPTY_FORM = {
  id: null,
  templateKey: null,
  title: "",
  description: "",
  type: EVENT_TYPE_KEYS.PUBLIC,
  intention: "",
  coverImage: "",
  tags: [],
  startAt: "",
  endAt: "",
  recurrence: null,
  durationMinutes: null,
  location: "",
  island: "",
  latitude: null,
  longitude: null,
  locationMode: LOCATION_MODES.BUSINESS,
  locationListingId: null,
  capacity: "",
  pricingModel: "free",
  ticketPriceCents: 0,
  minTier: "",
  doubloonReward: 0,
  graceWindowMinutes: { before: 15, after: 30 },
  suggestedRedeemable: null,
};

function formFromEvent(ev) {
  if (!ev) return { ...EMPTY_FORM };
  return {
    ...EMPTY_FORM,
    id: ev.id,
    templateKey: ev.templateKey || null,
    title: ev.title || "",
    description: ev.description || "",
    type: ev.type || EVENT_TYPE_KEYS.PUBLIC,
    intention: ev.intention || "",
    coverImage: ev.coverImage || "",
    tags: Array.isArray(ev.tags) ? ev.tags : [],
    startAt: toLocalInputValue(ev.startAt),
    endAt: toLocalInputValue(ev.endAt),
    recurrence: ev.recurrence || null,
    durationMinutes: ev.occurrenceDurationMinutes || null,
    location: ev.location || "",
    island: ev.island || "",
    latitude: Number.isFinite(ev.latitude) ? ev.latitude : null,
    longitude: Number.isFinite(ev.longitude) ? ev.longitude : null,
    // Editing an existing event defaults to whatever mode the saved data
    // implies: coords > address > business fallback. The user can switch.
    locationMode: Number.isFinite(ev.latitude) && Number.isFinite(ev.longitude)
      ? LOCATION_MODES.COORDINATES
      : ev.location
        ? LOCATION_MODES.ADDRESS
        : LOCATION_MODES.BUSINESS,
    locationListingId: ev.locationListingId || ev.businessId || null,
    capacity: ev.capacity || "",
    pricingModel: (ev.ticketPriceCents || 0) > 0 ? "ticketed" : (ev.capacity ? "rsvp" : "free"),
    ticketPriceCents: ev.ticketPriceCents ? ev.ticketPriceCents / 100 : 0,
    minTier: ev.minTier || "",
    doubloonReward: ev.doubloonReward || 0,
    graceWindowMinutes: ev.graceWindowMinutes || { before: 15, after: 30 },
  };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StepTemplate({ form, setForm, listing }) {
  const [activeCategory, setActiveCategory] = useState("drinks");
  const templates = useMemo(
    () => EVENT_TEMPLATES.filter((t) => t.category === activeCategory),
    [activeCategory],
  );
  const selected = form.templateKey ? getEventTemplate(form.templateKey) : null;
  return (
    <div className="eew-step eew-step--template">
      <p className="eew-step__hint">
        <FontAwesomeIcon icon={faInfoCircle} /> Pick a template to get a head
        start — every field stays editable in later steps.
      </p>
      <div className="eew-template-cats">
        {TEMPLATE_CATEGORIES.map((c) => (
          <button
            type="button"
            key={c.key}
            className={`eew-template-cat ${activeCategory === c.key ? "active" : ""}`}
            onClick={() => setActiveCategory(c.key)}
          >
            {c.label}
          </button>
        ))}
      </div>
      <div className="eew-template-grid">
        {templates.map((t) => {
          const isActive = form.templateKey === t.key;
          return (
            <button
              type="button"
              key={t.key}
              className={`eew-template-card ${isActive ? "active" : ""}`}
              onClick={() => setForm(applyTemplate(form, t, listing))}
            >
              <FontAwesomeIcon icon={t.icon} className="eew-template-card__icon" />
              <div className="eew-template-card__body">
                <strong>{t.label}</strong>
                <p>{t.shortDescription}</p>
              </div>
            </button>
          );
        })}
      </div>
      {selected && (
        <div className="eew-template-helpful">
          <FontAwesomeIcon icon={faStar} /> <strong>{selected.label}</strong>:{" "}
          {selected.helpfulNotes}
        </div>
      )}
    </div>
  );
}

function StepBasics({ form, setForm }) {
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="eew-step">
      <label className="eew-field">
        <span>Title<span className="req">*</span></span>
        <input
          type="text"
          value={form.title}
          maxLength={200}
          onChange={(e) => update("title", e.target.value)}
        />
        <small>Make it punchy — this is what travelers see in lists.</small>
      </label>

      <div className="eew-row">
        <label className="eew-field">
          <span>Event Type<span className="req">*</span></span>
          <select value={form.type} onChange={(e) => update("type", e.target.value)}>
            {EVENT_TYPES.map((t) => (
              <option key={t.key} value={t.key}>{t.label}</option>
            ))}
          </select>
        </label>
        <label className="eew-field">
          <span>Intention</span>
          <select value={form.intention} onChange={(e) => update("intention", e.target.value)}>
            <option value="">— optional —</option>
            {EVENT_INTENTIONS.map((i) => (
              <option key={i} value={i}>{i}</option>
            ))}
          </select>
          <small>Helps us group analytics and surface similar events.</small>
        </label>
      </div>

      <label className="eew-field">
        <span><FontAwesomeIcon icon={faImage} /> Cover Image URL</span>
        <input
          type="url"
          value={form.coverImage}
          placeholder="https://…"
          onChange={(e) => update("coverImage", e.target.value)}
        />
      </label>

      <label className="eew-field">
        <span>Tags (comma separated)</span>
        <input
          type="text"
          value={form.tags.join(", ")}
          placeholder="e.g. happy-hour, live-music, family-friendly"
          onChange={(e) => update("tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
        />
      </label>

      <label className="eew-field">
        <span>Description<span className="req">*</span></span>
        <textarea
          rows={6}
          maxLength={5000}
          value={form.description}
          onChange={(e) => update("description", e.target.value)}
        />
      </label>
    </div>
  );
}

function StepWhen({ form, setForm }) {
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const recurrence = useMemo(
    () => form.recurrence || { freq: RECURRENCE_FREQ.ONE_TIME, interval: 1, byDay: [], exDates: [] },
    [form.recurrence],
  );
  const isSeries = recurrence.freq && recurrence.freq !== RECURRENCE_FREQ.ONE_TIME;
  const needsByDay = recurrence.freq === RECURRENCE_FREQ.WEEKLY
    || recurrence.freq === RECURRENCE_FREQ.BIWEEKLY
    || recurrence.freq === RECURRENCE_FREQ.MONTHLY_BY_WEEKDAY;

  const updateRule = (patch) => {
    const next = { ...(form.recurrence || {}), ...patch };
    if (next.freq === RECURRENCE_FREQ.ONE_TIME) {
      setForm((f) => ({ ...f, recurrence: null }));
    } else {
      setForm((f) => ({ ...f, recurrence: next }));
    }
  };

  const toggleByDay = (key) => {
    const cur = Array.isArray(recurrence.byDay) ? recurrence.byDay : [];
    const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    updateRule({ byDay: next });
  };

  const previewOccurrences = useMemo(() => {
    const seedMs = fromLocalInputValue(form.startAt);
    if (!seedMs) return [];
    const endMs = fromLocalInputValue(form.endAt);
    const durationMs = endMs && endMs > seedMs
      ? endMs - seedMs
      : (form.durationMinutes ? form.durationMinutes * 60000 : 60 * 60 * 1000);
    return expandRecurrence({
      seedStart: seedMs,
      durationMs,
      recurrence: isSeries ? recurrence : null,
      fromMs: Date.now() - 24 * 60 * 60 * 1000,
      limit: 5,
    });
  }, [form.startAt, form.endAt, form.durationMinutes, recurrence, isSeries]);

  return (
    <div className="eew-step">
      <div className="eew-row">
        <label className="eew-field">
          <span>Start<span className="req">*</span></span>
          <input
            type="datetime-local"
            value={form.startAt}
            onChange={(e) => update("startAt", e.target.value)}
          />
        </label>
        <label className="eew-field">
          <span>End</span>
          <input
            type="datetime-local"
            value={form.endAt}
            onChange={(e) => update("endAt", e.target.value)}
          />
        </label>
      </div>

      <label className="eew-field">
        <span><FontAwesomeIcon icon={faRepeat} /> Recurrence</span>
        <select
          value={recurrence.freq || RECURRENCE_FREQ.ONE_TIME}
          onChange={(e) => updateRule({ freq: e.target.value, interval: 1, byDay: [] })}
        >
          {RECURRENCE_FREQ_OPTIONS.map((o) => (
            <option key={o.key} value={o.key}>{o.label}</option>
          ))}
        </select>
        <small>For series, the start date above seeds the recurrence.</small>
      </label>

      {needsByDay && (
        <div className="eew-field">
          <span>Days of week<span className="req">*</span></span>
          <div className="eew-day-picker">
            {DAYS_OF_WEEK.map((d) => (
              <button
                type="button"
                key={d.key}
                className={`eew-day ${(recurrence.byDay || []).includes(d.key) ? "active" : ""}`}
                onClick={() => toggleByDay(d.key)}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {isSeries && (
        <div className="eew-row">
          <label className="eew-field">
            <span>End the series (optional)</span>
            <input
              type="date"
              value={recurrence.until ? new Date(recurrence.until).toISOString().slice(0, 10) : ""}
              onChange={(e) => updateRule({ until: e.target.value ? new Date(e.target.value).getTime() : null })}
            />
          </label>
          <label className="eew-field">
            <span>Max occurrences (optional)</span>
            <input
              type="number"
              min="1"
              max="365"
              value={recurrence.count || ""}
              onChange={(e) => updateRule({ count: e.target.value ? Number(e.target.value) : null })}
            />
          </label>
        </div>
      )}

      <div className="eew-preview">
        <strong>Next {previewOccurrences.length} occurrences:</strong>
        {previewOccurrences.length === 0 ? (
          <p>Pick a start date above to preview the schedule.</p>
        ) : (
          <ul>
            {previewOccurrences.map((occ) => (
              <li key={occ.startMs}>
                {new Date(occ.startMs).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                {" → "}
                {new Date(occ.endMs).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
              </li>
            ))}
          </ul>
        )}
        {isSeries && (
          <p className="eew-rule-summary">
            <FontAwesomeIcon icon={faInfoCircle} /> {describeRecurrence(recurrence) || "Recurring"}
          </p>
        )}
      </div>
    </div>
  );
}

function StepWhere({ form, setForm, listing, userListings }) {
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [showPicker, setShowPicker] = useState(false);

  // Build the business options list. We always include the active `listing`
  // (the one the dashboard sidebar is focused on) so the user has a sensible
  // default even before `userListings` resolves.
  const businesses = useMemo(() => {
    const map = new Map();
    if (Array.isArray(userListings)) {
      userListings.forEach((l) => l?.id && map.set(l.id, l));
    }
    if (listing?.id && !map.has(listing.id)) map.set(listing.id, listing);
    return Array.from(map.values());
  }, [listing, userListings]);

  // Pre-select the listing the user clicked "Add Event" from (or the active
  // listing in the sidebar) the first time this step renders without a
  // selection.
  useEffect(() => {
    if (
      form.locationMode === LOCATION_MODES.BUSINESS
      && !form.locationListingId
      && listing?.id
    ) {
      setForm((f) => ({ ...f, locationListingId: listing.id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing?.id]);

  const selectedBusiness = useMemo(
    () => businesses.find((b) => b.id === form.locationListingId) || null,
    [businesses, form.locationListingId],
  );

  const setMode = (mode) => setForm((f) => ({ ...f, locationMode: mode }));

  const handleConfirmPin = ({ lat, lng }) => {
    setForm((f) => ({
      ...f,
      latitude: lat,
      longitude: lng,
      // If the user picked a pin from BUSINESS mode (because the linked
      // business had no precise location), promote them to COORDINATES so
      // the explicit pin is preserved by buildPayload(). Without this the
      // BUSINESS branch in buildPayload would copy `null` back from the
      // linked listing and discard the just-picked coords.
      locationMode: f.locationMode === LOCATION_MODES.BUSINESS
        ? LOCATION_MODES.COORDINATES
        : f.locationMode,
    }));
    setShowPicker(false);
  };

  const hasCoords = Number.isFinite(form.latitude) && Number.isFinite(form.longitude);

  // Mirror buildPayload's location-mode branching so the user sees the
  // lat/lng that will actually be persisted before they hit Save. Keep
  // these two in sync if buildPayload's BUSINESS/ADDRESS/COORDINATES
  // logic changes.
  const effectivePin = (() => {
    if (form.locationMode === LOCATION_MODES.COORDINATES && hasCoords) {
      return { lat: form.latitude, lng: form.longitude, source: "picked" };
    }
    if (
      form.locationMode === LOCATION_MODES.BUSINESS
      && selectedBusiness
      && Number.isFinite(selectedBusiness.latitude)
      && Number.isFinite(selectedBusiness.longitude)
    ) {
      return {
        lat: selectedBusiness.latitude,
        lng: selectedBusiness.longitude,
        source: "business",
      };
    }
    return null;
  })();

  return (
    <div className="eew-step eew-step--where">
      <div className="eew-field">
        <span><FontAwesomeIcon icon={faStore} /> Linked business</span>
        <select
          value={form.locationListingId || ""}
          onChange={(e) => {
            const id = e.target.value || null;
            setForm((f) => ({
              ...f,
              locationListingId: id,
              // If we're in business mode, swapping the linked business should
              // refresh the implied address; otherwise leave the custom value.
              ...(f.locationMode === LOCATION_MODES.BUSINESS && {
                location: "",
                island: "",
                latitude: null,
                longitude: null,
              }),
            }));
          }}
        >
          <option value="">— none (custom location) —</option>
          {businesses.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
        <small>
          Pick which of your businesses hosts this event. We'll use that
          business's address and map pin by default.
        </small>
      </div>

      <div className="eew-mode-toggle" role="radiogroup" aria-label="Location source">
        <button
          type="button"
          role="radio"
          aria-checked={form.locationMode === LOCATION_MODES.BUSINESS}
          className={`eew-mode-toggle__opt ${form.locationMode === LOCATION_MODES.BUSINESS ? "active" : ""}`}
          onClick={() => setMode(LOCATION_MODES.BUSINESS)}
          disabled={!form.locationListingId}
          title={form.locationListingId ? "" : "Pick a linked business first"}
        >
          Use business location
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={form.locationMode === LOCATION_MODES.ADDRESS}
          className={`eew-mode-toggle__opt ${form.locationMode === LOCATION_MODES.ADDRESS ? "active" : ""}`}
          onClick={() => setMode(LOCATION_MODES.ADDRESS)}
        >
          Enter an address
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={form.locationMode === LOCATION_MODES.COORDINATES}
          className={`eew-mode-toggle__opt ${form.locationMode === LOCATION_MODES.COORDINATES ? "active" : ""}`}
          onClick={() => setMode(LOCATION_MODES.COORDINATES)}
        >
          Pick on map / lat-long
        </button>
      </div>

      {form.locationMode === LOCATION_MODES.BUSINESS && (
        <div className="eew-readonly-block">
          {selectedBusiness ? (
            <>
              <p>
                <FontAwesomeIcon icon={faMapMarkerAlt} />{" "}
                <strong>{selectedBusiness.name}</strong>
              </p>
              <p className="eew-readonly-block__line">
                {safeText(selectedBusiness.location)
                  || formatListingAddress(selectedBusiness.address)
                  || <em>No street address on file.</em>}
              </p>
              <p className="eew-readonly-block__line">
                Island: {selectedBusiness.island || <em>not set</em>}
                {(Number.isFinite(selectedBusiness.latitude) && Number.isFinite(selectedBusiness.longitude)) ? (
                  <>
                    {" · "}
                    Pin: {selectedBusiness.latitude.toFixed(4)},{" "}
                    {selectedBusiness.longitude.toFixed(4)}
                  </>
                ) : (
                  <> · <em>no precise map pin</em></>
                )}
              </p>
              {!(Number.isFinite(selectedBusiness.latitude) && Number.isFinite(selectedBusiness.longitude)) && (
                <div className="eew-warning">
                  <p>
                    <strong>This business has no map pin yet.</strong> Your event
                    will appear at the island's general area until you add one.
                  </p>
                  <p>
                    Add a precise pin to the business listing (recommended), or
                    pick a one-off pin for just this event:
                  </p>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowPicker(true)}
                  >
                    <FontAwesomeIcon icon={faLocationCrosshairs} /> Pick a pin
                    for this event
                  </button>
                </div>
              )}
              <small>
                To change these details, edit the business listing itself, or
                switch to "Enter an address" / "Pick on map" above to override
                just this event.
              </small>
            </>
          ) : (
            <p><em>Pick a linked business above to use its address.</em></p>
          )}
        </div>
      )}

      {form.locationMode === LOCATION_MODES.ADDRESS && (
        <>
          <label className="eew-field">
            <span><FontAwesomeIcon icon={faMapMarkerAlt} /> Address / location name</span>
            <input
              type="text"
              value={form.location}
              maxLength={200}
              onChange={(e) => update("location", e.target.value)}
              placeholder="e.g. 123 Waterfront Drive, Cruz Bay"
            />
            <small>This shows on the event page so attendees can navigate to it.</small>
          </label>
          <label className="eew-field">
            <span>Island</span>
            <select
              value={form.island}
              onChange={(e) => update("island", e.target.value)}
            >
              <option value="">— select —</option>
              {ISLANDS.map((i) => (
                <option key={i.key} value={i.key}>{i.label}</option>
              ))}
            </select>
            <small>
              Without a precise pin, the event drops on the island's centroid.
              Switch to "Pick on map" for an exact spot.
            </small>
          </label>
        </>
      )}

      {form.locationMode === LOCATION_MODES.COORDINATES && (
        <>
          <label className="eew-field">
            <span><FontAwesomeIcon icon={faMapMarkerAlt} /> Address (optional)</span>
            <input
              type="text"
              value={form.location}
              maxLength={200}
              onChange={(e) => update("location", e.target.value)}
              placeholder="Shown to attendees alongside the pin"
            />
          </label>
          <div className="eew-row">
            <label className="eew-field">
              <span>Latitude</span>
              <input
                type="number"
                step="0.00001"
                value={form.latitude ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  update("latitude", v === "" ? null : Number(v));
                }}
              />
            </label>
            <label className="eew-field">
              <span>Longitude</span>
              <input
                type="number"
                step="0.00001"
                value={form.longitude ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  update("longitude", v === "" ? null : Number(v));
                }}
              />
            </label>
          </div>
          <label className="eew-field">
            <span>Island</span>
            <select
              value={form.island}
              onChange={(e) => update("island", e.target.value)}
            >
              <option value="">— select —</option>
              {ISLANDS.map((i) => (
                <option key={i.key} value={i.key}>{i.label}</option>
              ))}
            </select>
          </label>
          <div className="eew-coord-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowPicker(true)}
            >
              <FontAwesomeIcon icon={faLocationCrosshairs} />{" "}
              {hasCoords ? "Adjust pin on map" : "Pick point on map"}
            </button>
            {hasCoords && (
              <span className="eew-coord-actions__current">
                Current pin: {form.latitude.toFixed(5)}, {form.longitude.toFixed(5)}
              </span>
            )}
          </div>
        </>
      )}

      {showPicker && (
        <EventLocationPickerModal
          initialLat={form.latitude ?? selectedBusiness?.latitude ?? null}
          initialLng={form.longitude ?? selectedBusiness?.longitude ?? null}
          island={form.island || selectedBusiness?.island}
          onCancel={() => setShowPicker(false)}
          onConfirm={handleConfirmPin}
        />
      )}

      <p className="eew-effective-pin">
        <strong>Map pin that will be saved:</strong>{" "}
        {effectivePin ? (
          <>
            {effectivePin.lat.toFixed(5)}, {effectivePin.lng.toFixed(5)}{" "}
            <span className="eew-effective-pin__source">
              ({effectivePin.source === "picked"
                ? "from your pin"
                : "from linked business"})
            </span>
          </>
        ) : (
          <em>
            none — event will appear at the island's general area
            {form.island ? "" : " (pick an island for a better fallback)"}.
          </em>
        )}
      </p>
    </div>
  );
}

function StepTickets({ form, setForm }) {
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="eew-step">
      <div className="eew-pricing-toggle">
        {[
          { key: "free", label: "Free, no headcount" },
          { key: "rsvp", label: "Free RSVP (track headcount)" },
          { key: "ticketed", label: "Paid ticket" },
        ].map((opt) => (
          <button
            type="button"
            key={opt.key}
            className={`eew-pricing-opt ${form.pricingModel === opt.key ? "active" : ""}`}
            onClick={() => update("pricingModel", opt.key)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {form.pricingModel === "ticketed" && (
        <label className="eew-field">
          <span><FontAwesomeIcon icon={faTicketAlt} /> Ticket price (USD)<span className="req">*</span></span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.ticketPriceCents}
            onChange={(e) => update("ticketPriceCents", Number(e.target.value))}
          />
        </label>
      )}

      <label className="eew-field">
        <span>Capacity (optional)</span>
        <input
          type="number"
          min="1"
          value={form.capacity}
          onChange={(e) => update("capacity", e.target.value)}
        />
        <small>Leave blank for unlimited.</small>
      </label>

      <label className="eew-field">
        <span><FontAwesomeIcon icon={faCrown} /> Members-only access (optional)</span>
        <select value={form.minTier} onChange={(e) => update("minTier", e.target.value)}>
          <option value="">Open to everyone</option>
          {GATING_TRAVELER_TIERS.map((t) => (
            <option key={t.key} value={t.key}>{t.name} and up</option>
          ))}
        </select>
        <small>Restrict RSVP / tickets to members at this tier or higher.</small>
      </label>

      <label className="eew-field">
        <span><FontAwesomeIcon icon={faGift} /> Doubloons awarded for attending</span>
        <input
          type="number"
          min="0"
          value={form.doubloonReward}
          onChange={(e) => update("doubloonReward", Number(e.target.value))}
        />
      </label>

      <div className="eew-row">
        <label className="eew-field">
          <span>Grace before (min)</span>
          <input
            type="number"
            min="0"
            max="240"
            value={form.graceWindowMinutes?.before ?? 15}
            onChange={(e) => update("graceWindowMinutes", { ...form.graceWindowMinutes, before: Number(e.target.value) })}
          />
        </label>
        <label className="eew-field">
          <span>Grace after (min)</span>
          <input
            type="number"
            min="0"
            max="240"
            value={form.graceWindowMinutes?.after ?? 30}
            onChange={(e) => update("graceWindowMinutes", { ...form.graceWindowMinutes, after: Number(e.target.value) })}
          />
        </label>
      </div>
      <small className="eew-hint">
        Grace windows control how early/late event-linked redeemables stay valid.
      </small>
    </div>
  );
}

function StepRedeemable({ form }) {
  // The canonical link lives on the redeemable item (eventId field), set
  // via the Redeemables admin. Here we just summarize and point the
  // business owner to the right place.
  return (
    <div className="eew-step">
      <p className="eew-step__hint">
        <FontAwesomeIcon icon={faInfoCircle} /> Want a token offer that ONLY
        works during this event? Save this event first, then open the
        <strong> Redeemables</strong> tab and pick this event from the
        "Link to Event" dropdown on any item.
      </p>
      {form.suggestedRedeemable && (
        <div className="eew-suggested">
          <strong>Suggested from template:</strong>
          <p>{form.suggestedRedeemable.name} — {form.suggestedRedeemable.tokenCost} doubloons</p>
          <p className="eew-muted">{form.suggestedRedeemable.description}</p>
          <small>You'll create this in the Redeemables tab after saving.</small>
        </div>
      )}
    </div>
  );
}

function StepReview({ form, existingEvent }) {
  const start = form.startAt ? new Date(fromLocalInputValue(form.startAt)) : null;
  const end = form.endAt ? new Date(fromLocalInputValue(form.endAt)) : null;
  const ruleSummary = describeRecurrence(form.recurrence);
  return (
    <div className="eew-step eew-step--review">
      <h3>{form.title || <em className="eew-muted">Untitled event</em>}</h3>
      <dl>
        <dt>Type</dt><dd>{form.type}</dd>
        {form.intention && (<><dt>Intention</dt><dd>{form.intention}</dd></>)}
        <dt>When</dt>
        <dd>
          {start ? start.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—"}
          {end && ` → ${end.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`}
          {ruleSummary && <div className="eew-muted">{ruleSummary}</div>}
        </dd>
        <dt>Where</dt><dd>{form.location || "—"} {form.island && `(${form.island})`}</dd>
        <dt>Pricing</dt>
        <dd>
          {form.pricingModel === "ticketed"
            ? `$${Number(form.ticketPriceCents).toFixed(2)}`
            : form.pricingModel === "rsvp"
              ? "Free RSVP"
              : "Free"}
        </dd>
        {form.capacity && (<><dt>Capacity</dt><dd>{form.capacity}</dd></>)}
        {form.doubloonReward > 0 && (<><dt>Doubloons</dt><dd>{form.doubloonReward}</dd></>)}
        {form.tags?.length > 0 && (<><dt>Tags</dt><dd>{form.tags.join(", ")}</dd></>)}
      </dl>
      {form.description && (
        <div className="eew-review-desc">{form.description}</div>
      )}

      {existingEvent?.id ? (
        <div className="eew-promote-section">
          <h4 className="eew-promote-heading">
            <FontAwesomeIcon icon={faStar} /> Promote this event
          </h4>
          <p className="eew-muted eew-promote-subhead">
            Boost reach with a paid promotion. Stacks compound durations; custom dates let you pick exact start/end.
          </p>
          <PromotionStackPanel
            targetType="event"
            targetId={existingEvent.id}
            target={existingEvent}
          />
        </div>
      ) : (
        <p className="eew-muted eew-promote-hint">
          <FontAwesomeIcon icon={faInfoCircle} /> Save your event first to add a paid promotion.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function EventEditorWizard({
  businessId,
  listing,
  userListings = [],
  initialEvent = null,
  onSaved,
  onCancel,
}) {
  const { showToast } = useToast();
  const [stepIdx, setStepIdx] = useState(initialEvent ? 1 : 0);
  const [form, setForm] = useState(() => formFromEvent(initialEvent));
  const [saving, setSaving] = useState(false);

  // If editing an existing event the user already knows what they want — skip
  // the template picker by default but allow them to revisit it.
  useEffect(() => {
    if (initialEvent) setForm(formFromEvent(initialEvent));
  }, [initialEvent]);

  const step = STEPS[stepIdx];

  const canAdvance = useMemo(() => {
    if (step.key === "basics") return !!form.title && !!form.description;
    if (step.key === "when") return !!form.startAt;
    if (step.key === "tickets") {
      if (form.pricingModel === "ticketed") return Number(form.ticketPriceCents) > 0;
      return true;
    }
    return true;
  }, [step.key, form]);

  const buildPayload = () => {
    const startMs = fromLocalInputValue(form.startAt);
    const endMs = fromLocalInputValue(form.endAt);
    const occurrenceDurationMinutes = form.durationMinutes
      || (startMs && endMs && endMs > startMs ? Math.floor((endMs - startMs) / 60000) : null);

    // Resolve the effective location based on which mode the user picked.
    // - business: copy address/island/pin from the linked business.
    // - address:  use the typed address + island (no precise pin).
    // - coords:   use the typed/picked lat-long and any optional address.
    let location = null;
    let island = null;
    let latitude = null;
    let longitude = null;
    const linked = userListings?.find?.((l) => l.id === form.locationListingId)
      || (form.locationListingId === listing?.id ? listing : null);

    if (form.locationMode === LOCATION_MODES.BUSINESS && linked) {
      // `linked.address` is a structured object on business listings;
      // `linked.location` is the back-compat composed string. Prefer the
      // string so the event's `location` field is never an object.
      location = linked.location || formatListingAddress(linked.address) || linked.name || "";
      island = linked.island || null;
      latitude = Number.isFinite(linked.latitude) ? linked.latitude : null;
      longitude = Number.isFinite(linked.longitude) ? linked.longitude : null;
    } else if (form.locationMode === LOCATION_MODES.ADDRESS) {
      location = form.location || "";
      island = form.island || null;
    } else {
      // coordinates mode
      location = form.location || "";
      island = form.island || null;
      latitude = Number.isFinite(form.latitude) ? form.latitude : null;
      longitude = Number.isFinite(form.longitude) ? form.longitude : null;
    }

    return {
      title: form.title,
      description: form.description,
      type: form.type,
      intention: form.intention || null,
      templateKey: form.templateKey || null,
      coverImage: form.coverImage || null,
      tags: form.tags || [],
      startAt: startMs,
      endAt: endMs,
      recurrence: form.recurrence,
      occurrenceDurationMinutes,
      location,
      island,
      latitude,
      longitude,
      locationListingId: form.locationListingId || null,
      capacity: form.capacity ? Number(form.capacity) : null,
      ticketPriceCents: form.pricingModel === "ticketed"
        ? Math.round(Number(form.ticketPriceCents) * 100)
        : 0,
      minTier: form.minTier || "",
      doubloonReward: Number(form.doubloonReward) || 0,
      graceWindowMinutes: form.graceWindowMinutes,
    };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      let res;
      if (form.id) {
        res = await updateEvent(form.id, payload);
      } else {
        res = await createEvent({ businessId, ...payload });
      }
      const status = res?.status || "draft";
      if (status === "published") {
        showToast("Event published.", "success");
      } else {
        const missing = (res?.missingFields || []).join(", ");
        showToast(
          missing
            ? `Saved as draft — missing: ${missing}`
            : "Saved as draft.",
          "info",
        );
      }
      onSaved?.(res);
    } catch (err) {
      showToast(err?.message || "Couldn't save event.", "error");
    } finally {
      setSaving(false);
    }
  };

  const renderStep = () => {
    switch (step.key) {
      case "template": return <StepTemplate form={form} setForm={setForm} listing={listing} />;
      case "basics":   return <StepBasics form={form} setForm={setForm} />;
      case "when":     return <StepWhen form={form} setForm={setForm} />;
      case "where":    return <StepWhere form={form} setForm={setForm} listing={listing} userListings={userListings} />;
      case "tickets":  return <StepTickets form={form} setForm={setForm} />;
      case "redeemable": return <StepRedeemable form={form} />;
      case "review":   return <StepReview form={form} existingEvent={initialEvent} />;
      default: return null;
    }
  };

  const atEnd = stepIdx === STEPS.length - 1;

  return (
    <div className="eew">
      <div className="eew__header">
        <h2><FontAwesomeIcon icon={faCalendarPlus} /> {form.id ? "Edit Event" : "New Event"}</h2>
        <button type="button" className="eew__close" onClick={onCancel} aria-label="Close">
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <ol className="eew__steps">
        {STEPS.map((s, i) => (
          <li
            key={s.key}
            className={`eew__step ${i === stepIdx ? "active" : ""} ${i < stepIdx ? "done" : ""}`}
            onClick={() => i < stepIdx && setStepIdx(i)}
          >
            <span className="eew__step-num">{i + 1}</span>
            <span className="eew__step-label">{s.label}</span>
          </li>
        ))}
      </ol>

      <div className="eew__body">{renderStep()}</div>

      <div className="eew__footer">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
          disabled={stepIdx === 0 || saving}
        >
          <FontAwesomeIcon icon={faChevronLeft} /> Back
        </button>
        {atEnd ? (
          <button
            type="button"
            className="btn-primary"
            disabled={saving || !form.title || !form.startAt}
            onClick={handleSave}
          >
            {saving ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving…</> : <><FontAwesomeIcon icon={faCheck} /> {form.id ? "Save Event" : "Create Event"}</>}
          </button>
        ) : (
          <button
            type="button"
            className="btn-primary"
            onClick={() => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1))}
            disabled={!canAdvance || saving}
          >
            Next <FontAwesomeIcon icon={faChevronRight} />
          </button>
        )}
      </div>
    </div>
  );
}
